import { useRef } from 'react';
import { FileType, NetworkFile } from '@/types/data';

export const useFiles = (
  addFileToNetwork: (networkId: string, file: Omit<NetworkFile, 'id'>) => boolean
) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const determineFileType = (fileName: string): FileType => {
    const lowerCaseName = fileName.toLowerCase();
    if (lowerCaseName.includes('serviceability')) return FileType.Serviceability;
    if (lowerCaseName.includes('hub')) return FileType.Hubs;
    if (lowerCaseName.includes('connection')) return FileType.Connections;

    // Default based on file extension or other criteria
    return FileType.Serviceability;
  };

  const handleFileUpload = (networkId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const newFile: Omit<NetworkFile, 'id'> = {
          name: file.name,
          type: determineFileType(file.name),
          content: content,
          dateAdded: new Date().toISOString(),
        };

        addFileToNetwork(networkId, newFile);
      };
      reader.readAsText(file);
    });
  };

  const triggerFileInput = (networkId: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.dataset.networkId = networkId;
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const networkId = e.target.dataset.networkId;
    if (networkId) {
      handleFileUpload(networkId, e.target.files);
    }
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const getFileIcon = (fileType: FileType) => {
    switch (fileType) {
      case FileType.Serviceability:
        return '/chart.svg'; // Chart icon for Serviceability
      case FileType.Hubs:
        return '/building.svg'; // Building icon for Hubs
      case FileType.Connections:
        return '/arrows.svg'; // Arrows icon for Connections
      default:
        return '/file.svg'; // Default file icon
    }
  };

  return {
    fileInputRef,
    determineFileType,
    handleFileUpload,
    triggerFileInput,
    handleFileInputChange,
    getFileIcon,
  };
};
